/**
 * Project Types
 *
 * Define os tipos TypeScript para o sistema de projetos,
 * similar à estrutura do Claude.
 */

/**
 * Representa um projeto no sistema
 */
export interface Project {
  id: string;                    // UUID gerado automaticamente
  name: string;                  // Nome do projeto (max 100 caracteres)
  description?: string;          // Descrição opcional (max 500 caracteres)
  custom_instructions?: string;  // Instruções customizadas (max 2000 caracteres)
  user_id: string;              // ID do usuário proprietário
  is_active: boolean;           // Se o projeto está ativo
  created_at: string;           // Timestamp de criação (ISO 8601)
  updated_at: string;           // Timestamp de última atualização
  chats_count?: number;         // Número de chats no projeto
  total_messages?: number;      // Total de mensagens em todos os chats
}

export interface ProjectDocument {
  id: number;
  project_id: string;
  uploaded_by: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  token_count: number;
  content_preview?: string;
  vector_ids: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Request para criação de novo projeto
 */
export interface CreateProjectRequest {
  name: string;                  // Nome do projeto (obrigatório, max 100 caracteres)
  description?: string;          // Descrição opcional (max 500 caracteres)
  custom_instructions?: string;  // Instruções customizadas (max 2000 caracteres)
}

/**
 * Request para atualização de projeto existente
 */
export interface UpdateProjectRequest {
  name?: string;                 // Novo nome (max 100 caracteres)
  description?: string;          // Nova descrição (max 500 caracteres)
  custom_instructions?: string;  // Novas instruções customizadas (max 2000 caracteres)
  is_active?: boolean;          // Atualizar status ativo
}

/**
 * Response da API com projeto criado/atualizado
 */
export interface ProjectResponse {
  success: boolean;
  message: string;
  data: Project;
}

/**
 * Response da API com lista de projetos (paginada)
 */
export interface ProjectsListResponse {
  success: boolean;
  message: string;
  data: Project[];
  metadata?: {
    total: number;
    page: number;
    pages: number;
    has_more: boolean;
  };
}

/**
 * Contexto de projeto ativo na aplicação
 */
export interface ProjectContext {
  currentProject: Project | null;
  projects: Project[];
  isLoading: boolean;
  error: string | null;

  // Ações
  createProject: (data: CreateProjectRequest) => Promise<Project>;
  updateProject: (projectId: string, data: UpdateProjectRequest) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  setActiveProject: (projectId: string | null) => Promise<void>; // null = deactivate all
  loadProjects: () => Promise<void>;
  clearError: () => void;
}

/**
 * Validação de campos do formulário de projeto
 */
export interface ProjectFormErrors {
  name?: string;
  description?: string;
  custom_instructions?: string;
}

/**
 * Constantes de validação
 */
export const PROJECT_VALIDATION = {
  NAME_MAX_LENGTH: 100,
  NAME_MIN_LENGTH: 1,
  DESCRIPTION_MAX_LENGTH: 500,
  CUSTOM_INSTRUCTIONS_MAX_LENGTH: 2000,
} as const;

/**
 * Projeto com informações detalhadas (incluindo chats)
 */
export interface ProjectDetail extends Project {
  chats: {
    id: number;
    session_id: string;
    chat_title: string;
    created_at: string;
    updated_at: string;
    messages_count: number;
    token_usage: number;
    agent_name?: string;
  }[];
  documents: ProjectDocument[];
}

/**
 * Response da API com detalhes do projeto
 */
export interface ProjectDetailResponse {
  success: boolean;
  message: string;
  data: ProjectDetail;
}
